import { useEffect, useMemo, useState } from "react";
import { t } from "../i18n/index.js";

/* ================================================================
   Begrenzte Listen — Unterlisten in Detailansichten zeigen zuerst
   nur einen Teil (Grenzen in kern/konstanten.js, LISTEN_GRENZEN).

   Drei Formen:
   - `useBegrenzt` + `<MehrZeigen>`: Tabellen und Kartenraster. Unten
     steht „12 von 140“ mit „Weitere 24 zeigen“ und „Alle zeigen“.
   - `<KurzListe>`: Aufzählungen im Fließtext (Winzer einer Region,
     Jahrgänge in einer Zelle). „+ 9 weitere“ klappt an Ort und Stelle
     auf, „weniger“ wieder zu.
   - `kurzText()`: reiner Text, wo nichts anklickbar sein darf (die
     Länderkarte ist selbst ein Knopf).

   Die Hauptlisten (Keller, Weine, Winzer) wachsen beim Scrollen von
   selbst nach (useNachwachsen) — dort gibt es keinen Knopf.

   Kein eigener Import aus primitive.jsx: Die Bausteine dort benutzen
   diese hier (JahrgangsLinks), umgekehrt gäbe es einen Kreis.
   ================================================================ */

/** Ausschnitt einer Liste. `schluessel` setzt auf die erste Portion
    zurück — beim Wechsel des Objekts oder eines Filters. */
function useBegrenzt(liste, { erste, schritt = erste, schluessel = "" }) {
  const [anzahl, setAnzahl] = useState(erste);
  useEffect(() => { setAnzahl(erste); }, [schluessel, erste]);
  const gesamt = liste.length;
  const sichtbar = useMemo(
    () => (anzahl >= gesamt ? liste : liste.slice(0, anzahl)),
    [liste, anzahl, gesamt]);
  return {
    sichtbar,
    gesamt,
    rest: Math.max(0, gesamt - sichtbar.length),
    schritt,
    mehr: () => setAnzahl((n) => n + schritt),
    alle: () => setAnzahl(Infinity),
  };
}

/** Fußzeile unter einer begrenzten Liste. Erscheint nur, wenn etwas
    fehlt. */
function MehrZeigen({ b }) {
  if (!b || b.rest <= 0) return null;
  const naechste = Math.min(b.schritt, b.rest);
  return (
    <div className="wl-mehrzeigen">
      <span className="wl-mehrzeigen-stand num">{t("{n} von {gesamt}", { n: b.sichtbar.length, gesamt: b.gesamt })}</span>
      <button type="button" className="wl-textknopf" onClick={b.mehr}>
        {t("Weitere {n} zeigen", { n: naechste })}
      </button>
      {b.rest > naechste && (
        <button type="button" className="wl-textknopf ist-dezent" onClick={b.alle}>
          {t("Alle {n} zeigen", { n: b.gesamt })}
        </button>
      )}
    </div>
  );
}

/** Dasselbe als Baustein — für Stellen, an denen ein Hook nicht
    hinpasst (Zweige mit frühem return). `children` bekommt den
    sichtbaren Ausschnitt und liefert die Liste; die Fußzeile hängt
    der Baustein selbst an. */
function Begrenzt({ liste, grenze, schluessel = "", children }) {
  const b = useBegrenzt(liste, { ...grenze, schluessel });
  return (
    <>
      {children(b.sichtbar)}
      <MehrZeigen b={b} />
    </>
  );
}

/** Aufzählung im Fließtext. `elemente` sind fertige Knoten (mit key);
    `immer` sind Schlüssel, die auch zugeklappt sichtbar bleiben (etwa
    ein gerade aktiver Filterchip). */
function KurzListe({ elemente, grenze, trenner = null, immer = null, className = "" }) {
  const [offen, setOffen] = useState(false);
  const zeigen = offen || elemente.length <= grenze
    ? elemente
    : elemente.filter((e, i) => i < grenze || (immer && immer.has(e.key)));
  const rest = elemente.length - zeigen.length;
  const mitTrenner = (liste) => (trenner == null ? liste
    : liste.flatMap((e, i) => (i === 0 ? [e] : [<span key={"t" + i} className="wl-kurztrenner">{trenner}</span>, e])));
  return (
    <span className={"wl-kurzliste " + className}>
      {mitTrenner(zeigen)}
      {rest > 0 && (
        <button type="button" className="wl-textknopf wl-kurzmehr" onClick={() => setOffen(true)}>
          {t("+ {n} weitere", { n: rest })}
        </button>
      )}
      {offen && elemente.length > grenze && (
        <button type="button" className="wl-textknopf ist-dezent wl-kurzmehr" onClick={() => setOffen(false)}>
          {t("weniger")}
        </button>
      )}
    </span>
  );
}

/** „A · B · C … und 9 weitere“ — für Stellen ohne Bedienung. */
function kurzText(namen, grenze, trenner = " · ") {
  if (namen.length <= grenze) return namen.join(trenner);
  return `${namen.slice(0, grenze).join(trenner)} ${t("… und {n} weitere", { n: namen.length - grenze })}`;
}

export { Begrenzt, KurzListe, MehrZeigen, kurzText, useBegrenzt };
